import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { getQuestionLogEntry, listQuestionLog } from "@hp/api-client";
import type { QuestionLogEntry, QuestionTrace, TracePassage, TracePart } from "@hp/api-client";
import { useAuth } from "../auth/AuthContext";
import { PageHeader } from "../components/layout/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../components/Feedback";
import { Fold, ObservabilitySections, Tag, TraceHeader } from "../components/trace/TracePanels";

type Range = "today" | "7d" | "30d" | "all";

const RANGES: { key: Range; label: string; days: number | null }[] = [
  { key: "today", label: "Today", days: 1 },
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "all", label: "All", days: null },
];

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function answerOf(entry: QuestionLogEntry): string {
  return (entry.response.results ?? []).map((r) => r.answer ?? "").filter(Boolean).join("\n\n");
}

/** The selector's "missing" note as text, whether the trace holds a sentence, a list or nothing. */
function missingText(value: string | string[] | null | undefined): string {
  if (Array.isArray(value)) return value.filter(Boolean).join("; ");
  return typeof value === "string" ? value.trim() : "";
}

function Passages({ items }: { items: TracePassage[] }) {
  if (!items.length) return <p className="text-xs text-zinc-500">None.</p>;
  return (
    <ol className="space-y-1">
      {items.map((p, i) => (
        <li key={`${p.key}-${i}`} className="text-xs text-zinc-700">
          <span className="font-mono text-zinc-500">{p.key || `#${i + 1}`}</span>{" "}
          <span className="font-medium text-zinc-900">{p.file}</span>
          {p.page != null ? <span className="text-zinc-500"> p.{p.page}</span> : null}
          {p.where ? <span className="text-zinc-500"> · {p.where}</span> : null}
          {p.layer ? <span className="text-zinc-500"> · {p.layer}</span> : null}
          <span className="text-zinc-400"> · score {p.score}</span>
          {p.party ? <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800">party</span> : null}
          {p.pinned ? <span className="ml-1 rounded bg-sky-50 px-1 text-[10px] text-sky-800">pinned</span> : null}
          {p.expand ? <span className="ml-1 rounded bg-zinc-100 px-1 text-[10px] text-zinc-700">expanded</span> : null}
          {p.annexure ? <span className="ml-1 text-zinc-500">[{p.annexure}]</span> : null}
          {p.text ? <div className="ml-6 text-zinc-500">{p.text}</div> : null}
        </li>
      ))}
    </ol>
  );
}

function Part({ part, index }: { part: TracePart; index: number }) {
  return (
    <div className="rounded-md bg-zinc-50 px-3 py-2">
      <p className="text-xs font-semibold text-zinc-800">
        Part {index + 1}: {part.question}
      </p>
      <p className="mt-0.5 text-xs text-zinc-500">
        {part.candidates} candidates · kept {part.kept?.length ?? 0}
        {missingText(part.missing) ? ` · missing: ${missingText(part.missing)}` : ""}
      </p>
      {part.kept?.length ? (
        <div className="mt-1">
          <Passages items={part.kept} />
        </div>
      ) : null}
      {part.more_searches?.length ? (
        <p className="mt-1 text-xs text-zinc-600">
          <span className="font-medium">Asked for more:</span> {part.more_searches.map((s) => `${s.text} [${s.layer}]`).join(" · ")}
        </p>
      ) : null}
      {part.second_round ? (
        <div className="mt-1 border-l-2 border-zinc-200 pl-2">
          <p className="text-xs text-zinc-600">
            Second round: {part.second_round.candidates} candidates · kept {part.second_round.kept.length}
            {missingText(part.second_round.missing) ? ` · still missing: ${missingText(part.second_round.missing)}` : ""}
          </p>
          <Passages items={part.second_round.kept} />
        </div>
      ) : null}
    </div>
  );
}

/** One question's trace, step by step: tokens, first search, the plan and its parts, the challenge, references, the passages the writer saw. */
function TraceView({ trace }: { trace: QuestionTrace }) {
  const timings = Object.entries(trace.timings ?? {});
  const calls = Object.entries(trace.calls ?? {});
  if (trace.answered_from) {
    return (
      <div className="space-y-2">
        <TraceHeader trace={trace} />
        <p className="text-xs text-zinc-600">
          Answered from {trace.answered_from} ({trace.method}); no search or model call.
        </p>
        <ObservabilitySections trace={trace} />
        <Fold title="Raw trace (JSON)">
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-[11px] text-zinc-700">{JSON.stringify(trace, null, 2)}</pre>
        </Fold>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <TraceHeader trace={trace} />
      <div className="flex flex-wrap gap-1.5 text-xs">
        {trace.answer_cache === "hit" ? <Tag tone="sky">served from the answer cache</Tag> : null}
        <Tag>{trace.agentic ? "deep research" : "single search"}</Tag>
        {trace.writer?.shape ? <Tag>shape: {trace.writer.shape}</Tag> : null}
        <Tag>{trace.model_calls ?? 0} model calls{calls.length ? ` (${calls.map(([k, v]) => `${k} ${v}`).join(", ")})` : ""}</Tag>
        {trace.seconds != null ? <Tag>{trace.seconds}s</Tag> : null}
        {trace.concept_sections ? <Tag>{trace.concept_sections} concept sections</Tag> : null}
        {trace.writer && !trace.writer.used_model ? <Tag tone="amber">writer not used{trace.writer.notice ? `: ${trace.writer.notice}` : ""}</Tag> : null}
      </div>
      {trace.tokens ? (
        <p className="text-xs text-zinc-700">
          <span className="font-semibold">Search words:</span> {trace.tokens.words.join(", ") || "none"}
          {trace.tokens.exact.length ? (
            <>
              {" "}
              · <span className="font-semibold">must appear:</span> {trace.tokens.exact.join(", ")}
            </>
          ) : null}
          {trace.as_of ? <span className="text-zinc-500"> · as of {trace.as_of}</span> : null}
        </p>
      ) : null}
      {timings.length ? (
        <p className="text-xs text-zinc-500">
          <span className="font-semibold text-zinc-700">Time:</span> {timings.map(([k, v]) => `${k} ${v}s`).join(" · ")}
        </p>
      ) : null}
      {trace.first_search ? (
        <Fold title="First search" hint="The first look through the documents for the question's words. Shows how many passages matched and the top ones." count={`${trace.first_search_count ?? trace.first_search.length} found, top ${trace.first_search.length} shown`}>
          <Passages items={trace.first_search} />
        </Fold>
      ) : null}
      {trace.plan ? (
        <Fold title="Research plan" hint="For hard questions, HP Genie first breaks the question into smaller questions and decides what to search for each." count={`${trace.plan.parts.length} parts`} open>
          <p className="text-xs text-zinc-700">
            <span className="font-semibold">Issue:</span> {trace.plan.issue}
          </p>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-xs text-zinc-700">
            {trace.plan.parts.map((p, i) => (
              <li key={i}>
                {p.question}
                <div className="text-zinc-500">{p.searches.map((s) => `${s.text} [${s.layer}]`).join(" · ")}</div>
              </li>
            ))}
          </ol>
        </Fold>
      ) : null}
      {trace.parts?.length ? (
        <Fold title="What each part found" hint="For each smaller question: how many passages the search found, which were kept, and what was still missing." count={trace.parts.length} open>
          <div className="space-y-2">
            {trace.parts.map((p, i) => (
              <Part key={i} part={p} index={i} />
            ))}
          </div>
        </Fold>
      ) : null}
      {trace.challenge ? (
        <Fold title="Challenge pass" hint="A second check after the search. It reads the passages against the question, notes what looks wrong or missing, and keeps only the passages that really matter. Kept is how many were left." count={`${trace.challenge.kept.length} kept`}>
          <p className="whitespace-pre-wrap text-xs text-zinc-700">{trace.challenge.reading}</p>
          <div className="mt-1">
            <Passages items={trace.challenge.kept} />
          </div>
        </Fold>
      ) : null}
      {trace.references?.length ? (
        <Fold title="Paragraphs followed from references" hint="When a passage says see clause 5, the paragraph it points to is fetched too." count={trace.references.length}>
          <Passages items={trace.references} />
        </Fold>
      ) : null}
      <ObservabilitySections trace={trace} />
      <Fold title="Raw trace (JSON)">
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-[11px] text-zinc-700">{JSON.stringify(trace, null, 2)}</pre>
      </Fold>
    </div>
  );
}

/** One logged question: who asked, what came back, and (opened) the full research trace fetched on demand. */
function Entry({ entry, defaultOpen = false }: { entry: QuestionLogEntry; defaultOpen?: boolean }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const detail = useQuery({ queryKey: ["genie-question", entry.id], queryFn: () => getQuestionLogEntry(entry.id), enabled: open });
  const answer = answerOf(entry);
  return (
    <li className="rounded-lg border border-zinc-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={entry.found ? "emerald" : "rose"}>{entry.found ? "Answered" : "Not found"}</Tag>
        {entry.cached ? <Tag tone="sky">cache</Tag> : null}
        {entry.shape ? <Tag>{entry.shape}</Tag> : null}
        <Tag>{entry.method || "—"}</Tag>
        <span className="text-xs text-zinc-500">
          {entry.user_name || entry.user_email} · {when(entry.created_at)} · {entry.seconds}s · {entry.model_calls} model call{entry.model_calls === 1 ? "" : "s"}
          {entry.turns ? ` · turn ${entry.turns + 1} of a chat` : ""}
        </span>
      </div>
      <p className="mt-2 text-sm font-medium text-zinc-900">{entry.question}</p>
      {answer ? <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-xs text-zinc-600">{answer}</p> : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
        How it was answered
      </button>
      {open ? (
        <div className="mt-2 space-y-3">
          {detail.isLoading ? <LoadingState label="Loading the trace..." /> : null}
          {detail.error ? <ErrorState error={detail.error} label="The trace could not be loaded" /> : null}
          {detail.data ? (
            <>
              {answer ? (
                <Fold title="Full answer">
                  <p className="whitespace-pre-wrap text-xs text-zinc-700">{answerOf(detail.data)}</p>
                </Fold>
              ) : null}
              {(detail.data.trace ?? []).length === 0 ? <p className="text-xs text-zinc-500">No trace was recorded for this question (asked before tracing was added).</p> : null}
              {(detail.data.trace ?? []).map((t, i) => (
                <div key={i}>
                  {(detail.data?.trace ?? []).length > 1 ? <p className="mb-1 text-xs font-semibold text-zinc-700">Question {i + 1}: {t.question}</p> : null}
                  <TraceView trace={t} />
                </div>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Genie questions (admins): every question asked, with its answer and the record of how it was answered, to study later. */
export function GenieQuestionLogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const openId = params.get("open");
  const pinned = useQuery({ queryKey: ["genie-question", openId, "pinned"], queryFn: () => getQuestionLogEntry(openId as string), enabled: isAdmin && !!openId });
  const [range, setRange] = React.useState<Range>("7d");
  const [onlyMissed, setOnlyMissed] = React.useState(false);
  const [needle, setNeedle] = React.useState("");
  const since = React.useMemo(() => {
    const days = RANGES.find((r) => r.key === range)?.days;
    if (!days) return undefined;
    const d = new Date();
    if (days === 1) d.setHours(0, 0, 0, 0);
    else d.setDate(d.getDate() - days);
    return d.toISOString();
  }, [range]);
  const query = useQuery({ queryKey: ["genie-questions", since], queryFn: () => listQuestionLog({ since, limit: 500 }), enabled: isAdmin });

  if (!isAdmin) {
    return (
      <div className="page-stack">
        <PageHeader title="Genie questions" description="Only an admin can read the questions put to HP Genie." />
      </div>
    );
  }
  const rows = (query.data ?? []).filter(
    (e) =>
      (!onlyMissed || !e.found) &&
      (!needle || e.question.toLowerCase().includes(needle.toLowerCase())),
  );
  const missed = (query.data ?? []).filter((e) => !e.found).length;
  const avg = query.data?.length ? (query.data.reduce((s, e) => s + e.seconds, 0) / query.data.length).toFixed(1) : null;
  return (
    <div className="page-stack">
      <PageHeader
        title="Genie questions"
        description="Every question put to HP Genie, with its answer and how it was answered: the search words, the research plan and its parts, what was kept and what the writer saw."
      />
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Which period" className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              role="tab"
              aria-selected={range === r.key}
              onClick={() => setRange(r.key)}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium",
                range === r.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
              ].join(" ")}
            >
              {r.label}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-zinc-700">
          <input type="checkbox" checked={onlyMissed} onChange={(e) => setOnlyMissed(e.target.checked)} /> Only questions nothing was found for
        </label>
        <label className="relative ml-auto block">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" aria-hidden />
          <input
            type="search"
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            placeholder="Find a question"
            className="rounded-md border border-zinc-200 py-1 pl-7 pr-2 text-xs"
          />
        </label>
      </div>
      {query.data ? (
        <p className="text-xs text-zinc-500">
          {query.data.length} question{query.data.length === 1 ? "" : "s"} · {missed} with nothing found{avg ? ` · ${avg}s on average` : ""}
        </p>
      ) : null}
      {query.isLoading ? <LoadingState label="Loading the questions..." /> : null}
      {query.error ? <ErrorState error={query.error} label="The questions could not be loaded" /> : null}
      {pinned.data && !rows.some((e) => e.id === pinned.data.id) ? (
        <ul className="mb-3 space-y-2">
          <Entry key={`pinned-${pinned.data.id}`} entry={pinned.data} defaultOpen />
        </ul>
      ) : null}
      {query.data && rows.length === 0 ? <EmptyState>No questions match.</EmptyState> : null}
      {rows.length ? (
        <ul className="space-y-2">
          {rows.map((e) => (
            <Entry key={e.id} entry={e} defaultOpen={e.id === openId} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
